import { useState } from "react";
import { Link, useParams } from "react-router-dom";

import { Badge } from "@/components/ui/Badge";
import { Button } from "@/components/ui/Button";
import {
  Affixed,
  Checkbox,
  Field,
  Input,
  Select,
  Textarea,
} from "@/components/ui/Field";
import { Page, PageHeader, Section } from "@/components/ui/Page";
import { EmptyState, ErrorState, Loading } from "@/components/ui/States";
import { cn } from "@/lib/cn";
import { BOOK_AGAIN_LABEL, type BookAgain } from "@/lib/crew";
import { formatDateShort, today, type IsoDate } from "@/lib/dates";
import { formatMoney, parseMoney, pluralise } from "@/lib/format";
import { jobName, TEAM_ROLE_LABEL, type TeamRole } from "@/lib/jobs";
import { describeError } from "@/lib/supabase";
import { useBoard } from "@/features/pipeline/queries";
import { bookingState, crewLink, seatBadge } from "./CrewBooking";
import {
  INVOICE_FILE_TYPES,
  openInvoiceFile,
  useAddPastWedding,
  useCrewHistory,
  useCrewInvoices,
  useCrewPayments,
  useCrewSeats,
  useDeleteInvoice,
  useDeletePayment,
  useRateSeat,
  useRemovePastWedding,
  useUpdatePastWedding,
  useRecordPayment,
  useSaveInvoice,
  useSaveTeamMember,
  useTeamMembers,
  type CrewHistory,
  type CrewInvoice,
  type CrewPayment,
  type CrewSeat,
  type TeamMember,
} from "./queries";
import { Star, Stars, statsFor } from "./TeamPage";

const METHOD_LABEL = { eft: "EFT", cash: "Cash", other: "Other" } as const;

const seatOwed = (seat: CrewSeat, paid: number) =>
  seat.fee === null || seat.status === "released"
    ? 0
    : Math.max(0, Number(seat.fee) + Number(seat.travel_fee ?? 0) - paid);

const weddingLabel = (seat: CrewSeat) =>
  seat.job
    ? `${formatDateShort(seat.job.event_date as IsoDate)} · ${jobName(seat.job)}`
    : "A removed wedding";

/**
 * One person in the crew database: their details and rates, every wedding
 * they shot with the agreement they signed and how Jena rated them, the
 * invoices they sent, and every payment made to them.
 */
export function TeamMemberPage() {
  const { id = "" } = useParams();
  const members = useTeamMembers(true);
  const seats = useCrewSeats();
  const payments = useCrewPayments();
  const invoices = useCrewInvoices();
  const history = useCrewHistory();

  if (members.isPending) return <Loading label="Loading" />;
  if (members.isError)
    return (
      <Page>
        <ErrorState
          title="This person could not load"
          detail={describeError(members.error)}
          onRetry={() => void members.refetch()}
        />
      </Page>
    );
  const member = members.data.find((m) => m.id === id);
  if (!member)
    return (
      <Page>
        <EmptyState
          title="Not on the team"
          body="This person isn't in the crew database. They may have been removed."
        />
      </Page>
    );

  const mySeats = (seats.data ?? [])
    .filter((seat) => seat.member_id === member.id)
    .sort((a, b) =>
      (b.job?.event_date ?? "").localeCompare(a.job?.event_date ?? ""),
    );
  const myPayments = (payments.data ?? []).filter(
    (payment) => payment.member_id === member.id,
  );
  const myInvoices = (invoices.data ?? []).filter(
    (invoice) => invoice.member_id === member.id,
  );
  const myHistory = (history.data ?? []).filter(
    (row) => row.member_id === member.id,
  );
  const stats = statsFor(
    member,
    seats.data ?? [],
    payments.data ?? [],
    history.data ?? [],
  );
  const paidFor = (seatId: string) =>
    myPayments
      .filter((payment) => payment.job_team_id === seatId)
      .reduce((sum, payment) => sum + Number(payment.amount), 0);

  return (
    <Page>
      <PageHeader
        eyebrow="Team"
        title={
          <span className="display">{member.legal_name ?? member.name}</span>
        }
        meta={
          <>
            {member.business_name ? <span>{member.business_name}</span> : null}
            {member.based_in ? <span>{member.based_in}</span> : null}
            <span>
              {pluralise(stats.weddings, "wedding")} shot
              {stats.upcoming ? `, ${stats.upcoming} coming up` : ""}
            </span>
            <Stars value={stats.averageRating} />
            {stats.owed > 0 ? (
              <span className="text-amber">
                {formatMoney(stats.owed)} still to pay
              </span>
            ) : null}
            <Link to="/settings/team" className="underline underline-offset-4">
              All the team
            </Link>
          </>
        }
      />

      <Details key={member.updated_at} member={member} />

      <Section
        title="Weddings"
        description="Every wedding they were booked for, with the agreement they signed. Rate each one after the wedding: the stars on the team list are the average."
      >
        {mySeats.length === 0 ? (
          <p className="text-[length:var(--text-sm)] text-taupe">
            Not booked on a wedding in the Studio yet. Add one below, or on the
            wedding page.
          </p>
        ) : (
          <div className="overflow-x-auto">
            <table className="ledger min-w-[56rem]">
              <thead>
                <tr>
                  <th className="label">Wedding</th>
                  <th className="label">Role</th>
                  <th className="label">Agreement</th>
                  <th className="label num">Fee + travel</th>
                  <th className="label num">Paid</th>
                  <th className="label">Rating</th>
                </tr>
              </thead>
              <tbody>
                {mySeats.map((seat) => (
                  <SeatRow
                    key={seat.id}
                    seat={seat}
                    member={member}
                    paid={paidFor(seat.id)}
                  />
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Section>

      <PastWeddings member={member} seats={mySeats} history={myHistory} />

      <Invoices member={member} seats={mySeats} invoices={myInvoices} />
      <Payments
        member={member}
        seats={mySeats}
        payments={myPayments}
        invoices={myInvoices}
        paidFor={paidFor}
      />
    </Page>
  );
}

function Details({ member }: { member: TeamMember }) {
  const save = useSaveTeamMember();
  const initial = {
    name: member.name,
    legal_name: member.legal_name ?? "",
    business_name: member.business_name ?? "",
    email: member.email ?? "",
    phone: member.phone ?? "",
    portfolio_url: member.portfolio_url ?? "",
    based_in: member.based_in ?? "",
    usual_role: (member.usual_role ?? "second_photographer") as TeamRole,
    does_photo: member.does_photo,
    does_video: member.does_video,
    lead_rate: member.lead_rate === null ? "" : String(member.lead_rate),
    second_rate: member.second_rate === null ? "" : String(member.second_rate),
    book_again: (member.book_again ?? "") as BookAgain | "",
    notes: member.notes ?? "",
    needs_agreement: member.needs_agreement,
  };
  const [form, setForm] = useState(initial);
  const set = <K extends keyof typeof form>(key: K, value: (typeof form)[K]) =>
    setForm((current) => ({ ...current, [key]: value }));
  const dirty = JSON.stringify(form) !== JSON.stringify(initial);
  const text = (value: string) => value.trim() || null;

  return (
    <Section
      title="Details"
      actions={
        <Button
          variant="ghost"
          size="sm"
          onClick={() =>
            save.mutate({
              id: member.id,
              name: member.name,
              active: !member.active,
            })
          }
        >
          {member.active ? "Hide from the team" : "Bring back"}
        </Button>
      }
    >
      <form
        className="grid grid-cols-1 gap-x-6 gap-y-4 sm:grid-cols-2 lg:grid-cols-3"
        onSubmit={(event) => {
          event.preventDefault();
          if (!form.name.trim()) return;
          save.mutate({
            id: member.id,
            name: form.name.trim(),
            legal_name: text(form.legal_name),
            business_name: text(form.business_name),
            email: text(form.email),
            phone: text(form.phone),
            portfolio_url: text(form.portfolio_url),
            based_in: text(form.based_in),
            usual_role: form.usual_role,
            does_photo: form.does_photo,
            does_video: form.does_video,
            lead_rate: parseMoney(form.lead_rate),
            second_rate: parseMoney(form.second_rate),
            book_again: form.book_again || null,
            notes: text(form.notes),
            needs_agreement: form.needs_agreement,
          });
        }}
      >
        <Field label="Name you call them">
          <Input
            value={form.name}
            onChange={(e) => set("name", e.target.value)}
          />
        </Field>
        <Field label="Full name" hint="As it should appear on agreements.">
          <Input
            value={form.legal_name}
            onChange={(e) => set("legal_name", e.target.value)}
          />
        </Field>
        <Field label="Business name">
          <Input
            value={form.business_name}
            onChange={(e) => set("business_name", e.target.value)}
          />
        </Field>
        <Field label="Email">
          <Input
            type="email"
            value={form.email}
            onChange={(e) => set("email", e.target.value)}
          />
        </Field>
        <Field label="Phone">
          <Input
            type="tel"
            value={form.phone}
            onChange={(e) => set("phone", e.target.value)}
          />
        </Field>
        <Field label="Website or Instagram">
          <Input
            value={form.portfolio_url}
            onChange={(e) => set("portfolio_url", e.target.value)}
          />
        </Field>
        <Field label="Based in" hint="Town or area, e.g. Stellenbosch.">
          <Input
            value={form.based_in}
            onChange={(e) => set("based_in", e.target.value)}
          />
        </Field>
        <Field label="Usually">
          <Select
            value={form.usual_role}
            onChange={(e) => set("usual_role", e.target.value as TeamRole)}
          >
            {(Object.keys(TEAM_ROLE_LABEL) as TeamRole[]).map((key) => (
              <option key={key} value={key}>
                {TEAM_ROLE_LABEL[key]}
              </option>
            ))}
          </Select>
        </Field>
        <Field
          label="Lead rate"
          hint="To associate-shoot a wedding. Fills in the fee when you book them as lead."
        >
          <Affixed prefix="R">
            <Input
              inputMode="decimal"
              value={form.lead_rate}
              onChange={(e) => set("lead_rate", e.target.value)}
            />
          </Affixed>
        </Field>
        <Field
          label="Second rate"
          hint="To second-shoot. Fills in the fee when you book them as second."
        >
          <Affixed prefix="R">
            <Input
              inputMode="decimal"
              value={form.second_rate}
              onChange={(e) => set("second_rate", e.target.value)}
            />
          </Affixed>
        </Field>
        <div className="flex flex-col gap-3 pt-1">
          <Checkbox
            label="Photo"
            checked={form.does_photo}
            onChange={(e) => set("does_photo", e.target.checked)}
          />
          <Checkbox
            label="Video"
            checked={form.does_video}
            onChange={(e) => set("does_video", e.target.checked)}
          />
          <Checkbox
            label="Signs a crew agreement for each wedding"
            checked={form.needs_agreement}
            onChange={(e) => set("needs_agreement", e.target.checked)}
          />
        </div>
        <Field
          label="Would you book them again?"
          hint='"No" warns you if you pick them for a wedding.'
        >
          <Select
            value={form.book_again}
            onChange={(e) =>
              set("book_again", e.target.value as BookAgain | "")
            }
          >
            <option value="">Not decided</option>
            {(Object.keys(BOOK_AGAIN_LABEL) as BookAgain[]).map((key) => (
              <option key={key} value={key}>
                {BOOK_AGAIN_LABEL[key]}
              </option>
            ))}
          </Select>
        </Field>
        <Field
          label="Private notes"
          hint="Only you see these: strengths, what to brief them on, what went wrong."
          className="sm:col-span-2 lg:col-span-3"
        >
          <Textarea
            rows={3}
            value={form.notes}
            onChange={(e) => set("notes", e.target.value)}
          />
        </Field>
        <div className="sm:col-span-2 lg:col-span-3">
          <Button
            type="submit"
            variant="primary"
            disabled={!dirty || !form.name.trim()}
            loading={save.isPending}
          >
            Save details
          </Button>
        </div>
      </form>
    </Section>
  );
}

function SeatRow({
  seat,
  member,
  paid,
}: {
  seat: CrewSeat;
  member: TeamMember;
  paid: number;
}) {
  const rate = useRateSeat();
  const [note, setNote] = useState(seat.rating_note ?? "");
  const state = bookingState(seat, member);
  const past = seat.job ? seat.job.event_date <= today() : false;
  const badge =
    state === "draft" && past
      ? { label: "No agreement", tone: "neutral" as const }
      : seatBadge(seat, state);
  const total =
    seat.fee === null ? null : Number(seat.fee) + Number(seat.travel_fee ?? 0);

  return (
    <tr className={cn(seat.status === "released" && "opacity-60")}>
      <td>
        {seat.job ? (
          <Link
            to={`/weddings/${seat.job.id}`}
            className="underline decoration-transparent underline-offset-4 hover:decoration-[var(--color-rule-strong)]"
          >
            {weddingLabel(seat)}
          </Link>
        ) : (
          weddingLabel(seat)
        )}
        {seat.status === "released" && seat.released_reason ? (
          <span className="block text-[length:var(--text-2xs)] text-taupe">
            {seat.released_by === "them" ? "They withdrew" : "Released"}:{" "}
            {seat.released_reason}
          </span>
        ) : null}
      </td>
      <td className="text-taupe">{TEAM_ROLE_LABEL[seat.role]}</td>
      <td>
        <span className="flex flex-wrap items-center gap-2">
          {badge ? <Badge tone={badge.tone}>{badge.label}</Badge> : null}
          {seat.agreement_token ? (
            <a
              href={crewLink(seat.agreement_token)}
              target="_blank"
              rel="noreferrer noopener"
              className="text-[length:var(--text-2xs)] underline underline-offset-4"
            >
              Read it
            </a>
          ) : null}
        </span>
      </td>
      <td className="num" data-numeric>
        {total === null ? "—" : formatMoney(total)}
      </td>
      <td
        className={cn(
          "num",
          total !== null && past && paid < total && seat.status !== "released"
            ? "text-amber"
            : "",
        )}
        data-numeric
      >
        {paid ? formatMoney(paid) : "—"}
      </td>
      <td>
        <div className="flex items-center gap-2">
          <div
            className="flex gap-0.5"
            role="radiogroup"
            aria-label="Rating for this wedding"
          >
            {[1, 2, 3, 4, 5].map((value) => (
              <button
                key={value}
                type="button"
                role="radio"
                aria-checked={seat.rating === value}
                aria-label={`${value} out of 5`}
                className="px-0.5 leading-none hover:opacity-70"
                onClick={() =>
                  rate.mutate({
                    id: seat.id,
                    rating: seat.rating === value ? null : value,
                  })
                }
              >
                <Star fill={seat.rating !== null && value <= seat.rating ? 1 : 0} size={16} />
              </button>
            ))}
          </div>
          <Input
            aria-label="Note on this wedding"
            placeholder="A note on their work"
            className="min-w-[10rem] flex-1"
            value={note}
            onChange={(e) => setNote(e.target.value)}
            onBlur={() =>
              note !== (seat.rating_note ?? "") &&
              rate.mutate({ id: seat.id, rating_note: note.trim() || null })
            }
          />
        </div>
      </td>
    </tr>
  );
}

function SeatSelect({
  seats,
  value,
  onChange,
}: {
  seats: CrewSeat[];
  value: string;
  onChange: (value: string) => void;
}) {
  return (
    <Select value={value} onChange={(e) => onChange(e.target.value)}>
      <option value="">Not for one wedding</option>
      {seats.map((seat) => (
        <option key={seat.id} value={seat.id}>
          {weddingLabel(seat)}
        </option>
      ))}
    </Select>
  );
}

function Invoices({
  member,
  seats,
  invoices,
}: {
  member: TeamMember;
  seats: CrewSeat[];
  invoices: CrewInvoice[];
}) {
  const save = useSaveInvoice();
  const remove = useDeleteInvoice();
  const [file, setFile] = useState<File | null>(null);
  const [number, setNumber] = useState("");
  const [date, setDate] = useState<string>(today());
  const [amount, setAmount] = useState("");
  const [seatId, setSeatId] = useState("");
  const [fileKey, setFileKey] = useState(0);
  const seatOf = (id: string | null) => seats.find((seat) => seat.id === id);

  return (
    <Section
      title="Invoices they sent"
      description="Upload each invoice (a PDF or a photo of it). Stored privately: invoices carry banking details."
    >
      {invoices.length ? (
        <ul className="mb-4 flex flex-col">
          {invoices.map((invoice) => {
            const seat = seatOf(invoice.job_team_id);
            return (
              <li
                key={invoice.id}
                className="grid grid-cols-1 items-center gap-2 border-b py-2 text-[length:var(--text-sm)] sm:grid-cols-[7rem_minmax(0,1fr)_8rem_auto]"
              >
                <span className="text-taupe">
                  {formatDateShort(invoice.invoice_date as IsoDate)}
                </span>
                <span>
                  {invoice.invoice_number
                    ? `Invoice ${invoice.invoice_number}`
                    : "Invoice"}
                  {seat ? (
                    <span className="text-taupe"> · {weddingLabel(seat)}</span>
                  ) : null}
                </span>
                <span className="num" data-numeric>
                  {formatMoney(Number(invoice.amount))}
                </span>
                <span className="flex gap-1">
                  {invoice.file_path ? (
                    <Button
                      size="sm"
                      variant="ghost"
                      onClick={() => void openInvoiceFile(invoice.file_path!)}
                    >
                      Open
                    </Button>
                  ) : null}
                  <Button
                    size="sm"
                    variant="ghost"
                    onClick={() => {
                      if (window.confirm("Remove this invoice and its file?"))
                        remove.mutate(invoice);
                    }}
                  >
                    Remove
                  </Button>
                </span>
              </li>
            );
          })}
        </ul>
      ) : null}

      <form
        className="grid grid-cols-1 items-end gap-3 sm:grid-cols-2 lg:grid-cols-[minmax(0,1.4fr)_minmax(0,1fr)_9rem_9rem_minmax(0,1.4fr)_auto]"
        onSubmit={(event) => {
          event.preventDefault();
          const value = parseMoney(amount);
          if (value === null) return;
          save.mutate(
            {
              member_id: member.id,
              amount: value,
              invoice_date: date,
              invoice_number: number.trim() || null,
              job_team_id: seatId || null,
              file,
            },
            {
              onSuccess: () => {
                setFile(null);
                setFileKey((k) => k + 1);
                setNumber("");
                setAmount("");
                setSeatId("");
              },
            },
          );
        }}
      >
        <Field label="File">
          <Input
            key={fileKey}
            type="file"
            accept={INVOICE_FILE_TYPES.join(",")}
            onChange={(e) => setFile(e.target.files?.[0] ?? null)}
          />
        </Field>
        <Field label="Their invoice number">
          <Input value={number} onChange={(e) => setNumber(e.target.value)} />
        </Field>
        <Field label="Date">
          <Input
            type="date"
            value={date}
            onChange={(e) => setDate(e.target.value)}
          />
        </Field>
        <Field label="Amount">
          <Affixed prefix="R">
            <Input
              inputMode="decimal"
              value={amount}
              onChange={(e) => {
                setAmount(e.target.value);
              }}
            />
          </Affixed>
        </Field>
        <Field label="For the wedding">
          <SeatSelect
            seats={seats}
            value={seatId}
            onChange={(id) => {
              setSeatId(id);
              const seat = seatOf(id);
              if (seat?.fee != null && !amount)
                setAmount(
                  String(Number(seat.fee) + Number(seat.travel_fee ?? 0)),
                );
            }}
          />
        </Field>
        <Button
          type="submit"
          disabled={parseMoney(amount) === null}
          loading={save.isPending}
        >
          Save invoice
        </Button>
      </form>
    </Section>
  );
}

function Payments({
  member,
  seats,
  payments,
  invoices,
  paidFor,
}: {
  member: TeamMember;
  seats: CrewSeat[];
  payments: CrewPayment[];
  invoices: CrewInvoice[];
  paidFor: (seatId: string) => number;
}) {
  const record = useRecordPayment();
  const remove = useDeletePayment();
  const [date, setDate] = useState<string>(today());
  const [amount, setAmount] = useState("");
  const [seatId, setSeatId] = useState("");
  const [method, setMethod] = useState<keyof typeof METHOD_LABEL>("eft");
  const [reference, setReference] = useState("");
  const seatOf = (id: string | null) => seats.find((seat) => seat.id === id);
  const outstanding = seats.filter(
    (seat) =>
      seat.job &&
      seat.job.event_date <= today() &&
      seatOwed(seat, paidFor(seat.id)) > 0,
  );
  const total = payments.reduce((sum, p) => sum + Number(p.amount), 0);

  return (
    <Section
      title="Payments to them"
      description={
        payments.length
          ? `${formatMoney(total)} paid in total. A wedding shows as paid once its payments cover the fee and travel.`
          : "Record each payment you make. A wedding shows as paid once its payments cover the fee and travel."
      }
    >
      {outstanding.length ? (
        <ul className="mb-4 flex flex-col gap-1">
          {outstanding.map((seat) => {
            const owed = seatOwed(seat, paidFor(seat.id));
            const invoice = invoices.find((i) => i.job_team_id === seat.id);
            return (
              <li
                key={seat.id}
                className="flex flex-wrap items-center gap-2 text-[length:var(--text-sm)]"
              >
                <span className="text-amber">
                  {formatMoney(owed)} still to pay for {weddingLabel(seat)}
                </span>
                <Button
                  size="sm"
                  variant="secondary"
                  loading={record.isPending}
                  onClick={() =>
                    record.mutate({
                      member_id: member.id,
                      amount: owed,
                      paid_on: today(),
                      job_team_id: seat.id,
                      invoice_id: invoice?.id ?? null,
                    })
                  }
                >
                  Paid today
                </Button>
              </li>
            );
          })}
        </ul>
      ) : null}

      {payments.length ? (
        <ul className="mb-4 flex flex-col">
          {payments.map((payment) => {
            const seat = seatOf(payment.job_team_id);
            return (
              <li
                key={payment.id}
                className="grid grid-cols-1 items-center gap-2 border-b py-2 text-[length:var(--text-sm)] sm:grid-cols-[7rem_minmax(0,1fr)_8rem_auto]"
              >
                <span className="text-taupe">
                  {formatDateShort(payment.paid_on as IsoDate)}
                </span>
                <span>
                  {METHOD_LABEL[payment.method as keyof typeof METHOD_LABEL]}
                  {payment.reference ? ` · ${payment.reference}` : ""}
                  {seat ? (
                    <span className="text-taupe"> · {weddingLabel(seat)}</span>
                  ) : null}
                </span>
                <span className="num" data-numeric>
                  {formatMoney(Number(payment.amount))}
                </span>
                <Button
                  size="sm"
                  variant="ghost"
                  onClick={() => {
                    if (window.confirm("Remove this payment from the record?"))
                      remove.mutate(payment.id);
                  }}
                >
                  Remove
                </Button>
              </li>
            );
          })}
        </ul>
      ) : null}

      <form
        className="grid grid-cols-1 items-end gap-3 sm:grid-cols-2 lg:grid-cols-[9rem_9rem_minmax(0,1.4fr)_8rem_minmax(0,1fr)_auto]"
        onSubmit={(event) => {
          event.preventDefault();
          const value = parseMoney(amount);
          if (value === null || value <= 0) return;
          record.mutate(
            {
              member_id: member.id,
              amount: value,
              paid_on: date,
              job_team_id: seatId || null,
              invoice_id:
                invoices.find((i) => seatId && i.job_team_id === seatId)?.id ??
                null,
              method,
              reference: reference.trim() || null,
            },
            {
              onSuccess: () => {
                setAmount("");
                setSeatId("");
                setReference("");
              },
            },
          );
        }}
      >
        <Field label="Paid on">
          <Input
            type="date"
            value={date}
            onChange={(e) => setDate(e.target.value)}
          />
        </Field>
        <Field label="Amount">
          <Affixed prefix="R">
            <Input
              inputMode="decimal"
              value={amount}
              onChange={(e) => setAmount(e.target.value)}
            />
          </Affixed>
        </Field>
        <Field label="For the wedding">
          <SeatSelect seats={seats} value={seatId} onChange={setSeatId} />
        </Field>
        <Field label="How">
          <Select
            value={method}
            onChange={(e) =>
              setMethod(e.target.value as keyof typeof METHOD_LABEL)
            }
          >
            {(Object.keys(METHOD_LABEL) as (keyof typeof METHOD_LABEL)[]).map(
              (key) => (
                <option key={key} value={key}>
                  {METHOD_LABEL[key]}
                </option>
              ),
            )}
          </Select>
        </Field>
        <Field label="Reference">
          <Input
            value={reference}
            onChange={(e) => setReference(e.target.value)}
          />
        </Field>
        <Button
          type="submit"
          disabled={!(Number(parseMoney(amount)) > 0)}
          loading={record.isPending}
        >
          Record payment
        </Button>
      </form>
    </Section>
  );
}

/**
 * Weddings added by hand: one in the Studio they weren't on yet (becomes a
 * normal seat in the table above), or one from before the Studio, kept here
 * and counted in their record (never owed: those were settled).
 */
function PastWeddings({
  member,
  seats,
  history,
}: {
  member: TeamMember;
  seats: CrewSeat[];
  history: CrewHistory[];
}) {
  const board = useBoard();
  const add = useAddPastWedding();
  const updateRow = useUpdatePastWedding();
  const remove = useRemovePastWedding();
  const blank = {
    job_id: "",
    couple: "",
    event_date: "",
    venue: "",
    role: (member.usual_role ?? "second_photographer") as TeamRole,
    fee: "",
  };
  const [form, setForm] = useState(blank);
  const set = <K extends keyof typeof form>(key: K, value: (typeof form)[K]) =>
    setForm((current) => ({ ...current, [key]: value }));

  const onIt = new Set(seats.map((seat) => seat.job_id));
  const weddings = (board.data ?? [])
    .filter((job) => !onIt.has(job.id))
    .sort((a, b) => b.event_date.localeCompare(a.event_date));
  const outside = form.job_id === "outside";
  const ready = outside
    ? form.couple.trim() !== "" && form.event_date !== ""
    : form.job_id !== "";

  function submit() {
    if (!ready) return;
    const fee = parseMoney(form.fee);
    if (outside) {
      add.mutate({
        type: "history",
        row: {
          member_id: member.id,
          couple: form.couple.trim(),
          event_date: form.event_date,
          venue: form.venue.trim() || null,
          role: form.role,
          fee,
          rating: null,
          note: null,
        },
      });
    } else {
      add.mutate({
        type: "studio",
        job_id: form.job_id,
        member_id: member.id,
        role: form.role,
        fee,
      });
    }
    setForm(blank);
  }

  return (
    <Section
      title="Add a wedding they shot"
      description="Pick a wedding in the Studio, or choose “Not in the Studio” for one from before it existed."
    >
      {history.length > 0 ? (
        <div className="mb-5 overflow-x-auto">
          <p className="label mb-1">Before the Studio</p>
          <table className="ledger min-w-[44rem]">
            <thead>
              <tr>
                <th className="label">Wedding</th>
                <th className="label">Venue</th>
                <th className="label">Role</th>
                <th className="label num">Fee</th>
                <th className="label">Rating</th>
                <th />
              </tr>
            </thead>
            <tbody>
              {history.map((row) => (
                <tr key={row.id}>
                  <td className="whitespace-nowrap">
                    {formatDateShort(row.event_date as IsoDate)} ·{" "}
                    <span className="display">{row.couple}</span>
                  </td>
                  <td className="text-taupe">{row.venue ?? "—"}</td>
                  <td className="text-taupe">
                    {row.role ? TEAM_ROLE_LABEL[row.role as TeamRole] : "—"}
                  </td>
                  <td className="num" data-numeric>
                    {row.fee === null ? "—" : formatMoney(Number(row.fee))}
                  </td>
                  <td>
                    <Select
                      aria-label={`Rating for ${row.couple}`}
                      value={row.rating === null ? "" : String(row.rating)}
                      onChange={(event) =>
                        updateRow.mutate({
                          id: row.id,
                          rating: event.target.value
                            ? Number(event.target.value)
                            : null,
                        })
                      }
                      className="w-24"
                    >
                      <option value="">—</option>
                      {[5, 4, 3, 2, 1].map((value) => (
                        <option key={value} value={value}>
                          {"★".repeat(value)}
                        </option>
                      ))}
                    </Select>
                  </td>
                  <td className="text-right">
                    <Button
                      variant="ghost"
                      size="sm"
                      aria-label={`Remove ${row.couple}`}
                      onClick={() => remove.mutate(row.id)}
                    >
                      Remove
                    </Button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      ) : null}

      <form
        className="grid grid-cols-1 items-end gap-x-6 gap-y-4 sm:grid-cols-2 lg:grid-cols-3"
        onSubmit={(event) => {
          event.preventDefault();
          submit();
        }}
      >
        <Field label="Wedding">
          <Select
            value={form.job_id}
            onChange={(event) => set("job_id", event.target.value)}
          >
            <option value="">Choose…</option>
            <option value="outside">Not in the Studio (before it existed)</option>
            {weddings.map((job) => (
              <option key={job.id} value={job.id}>
                {formatDateShort(job.event_date)} · {jobName(job)}
              </option>
            ))}
          </Select>
        </Field>
        {outside ? (
          <>
            <Field label="Couple">
              <Input
                placeholder="e.g. Anna & Ben"
                value={form.couple}
                onChange={(event) => set("couple", event.target.value)}
              />
            </Field>
            <Field label="Date">
              <Input
                type="date"
                value={form.event_date}
                onChange={(event) => set("event_date", event.target.value)}
              />
            </Field>
            <Field label="Venue">
              <Input
                value={form.venue}
                onChange={(event) => set("venue", event.target.value)}
              />
            </Field>
          </>
        ) : null}
        <Field label="Role">
          <Select
            value={form.role}
            onChange={(event) => set("role", event.target.value as TeamRole)}
          >
            {(Object.keys(TEAM_ROLE_LABEL) as TeamRole[]).map((key) => (
              <option key={key} value={key}>
                {TEAM_ROLE_LABEL[key]}
              </option>
            ))}
          </Select>
        </Field>
        <Field label="Fee" hint="Optional.">
          <Affixed prefix="R">
            <Input
              inputMode="decimal"
              value={form.fee}
              onChange={(event) => set("fee", event.target.value)}
            />
          </Affixed>
        </Field>
        <div>
          <Button type="submit" disabled={!ready} loading={add.isPending}>
            Add the wedding
          </Button>
        </div>
      </form>
    </Section>
  );
}
